import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, ExternalLink, ArrowLeft, RefreshCw, X } from 'lucide-react';
import { adminListServers, adminSaveServer, adminDeleteServer } from '../site/serversApi';
import CoinLoader from '../components/CoinLoader';
import { serverPath } from '../site/routes';
import { GAMES, GAME_LABELS, formatMad } from '../data/servers';

const slugify = (text) => String(text || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  .slice(0, 80);

const TYPES = ['Classique', 'Monocompte', 'Épique', 'Francophone', 'International'];
const EMPTY = { name: '', slug: '', game: 'DOFUS 3', type: 'Classique', description: '', priceMode: 'auto', manualBuy: '', manualSell: '', enabled: true };

const inputClass = 'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none';
const priceText = (n) => (n == null ? '—' : formatMad(n));

function SessionExpired() {
  return (
    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 text-sm text-orange-300">
      Votre session admin a expiré. <Link to="/login" className="underline font-semibold">Reconnectez-vous</Link> pour gérer les serveurs.
    </div>
  );
}

function ServerEditor({ token, server, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!server) { setForm(EMPTY); setSlugTouched(false); return; }
    setForm({
      name: server.name,
      slug: server.slug,
      game: server.game,
      type: server.type,
      description: server.description || '',
      priceMode: server.price_mode === 'manual' ? 'manual' : 'auto',
      manualBuy: server.manual_buy ?? '',
      manualSell: server.manual_sell ?? '',
      enabled: !!server.enabled,
    });
    setSlugTouched(true);
  }, [server]);

  const update = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'name' && !slugTouched ? { slug: slugify(value) } : {}),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await adminSaveServer(token, server?.id, {
        ...form,
        manualBuy: form.manualBuy === '' ? null : form.manualBuy,
        manualSell: form.manualSell === '' ? null : form.manualSell,
      });
      onDone(true);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-gray-900 border border-yellow-500/40 rounded-2xl p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base sm:text-lg font-bold text-white">{server ? `Modifier ${server.name}` : 'Nouveau serveur'}</h3>
        <button type="button" onClick={() => onDone(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
      </div>
      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Jeu</label>
          <select value={form.game} onChange={update('game')} className={inputClass}>
            {GAMES.map(g => <option key={g} value={g}>{GAME_LABELS[g]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Nom du serveur *</label>
          <input value={form.name} onChange={update('name')} required placeholder="Ex : Nouveau serveur" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Slug (URL)</label>
          <input value={form.slug} onChange={(e) => { setSlugTouched(true); update('slug')(e); }} placeholder="nouveau-serveur" className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Type</label>
          <select value={form.type} onChange={update('type')} className={inputClass}>
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Description (page du serveur)</label>
        <textarea value={form.description} onChange={update('description')} rows={2} className={inputClass}
          placeholder="Courte description affichée sur la page du serveur" />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Mode de prix</label>
          <select value={form.priceMode} onChange={update('priceMode')} className={inputClass}>
            <option value="auto">Automatique (TryAndJudge ±)</option>
            <option value="manual">Manuel</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {form.priceMode === 'auto'
              ? 'Prix calculés depuis TryAndJudge. Les prix manuels servent de secours si le serveur n\'y figure pas.'
              : 'Les prix saisis remplacent le calcul automatique.'}
          </p>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Prix achat client (DH/M)</label>
          <input type="number" step="0.01" min="0.01" value={form.manualBuy} onChange={update('manualBuy')}
            placeholder={form.priceMode === 'manual' ? 'Ex : 35.00' : 'Secours, optionnel'}
            required={form.priceMode === 'manual'} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Prix vente client (DH/M)</label>
          <input type="number" step="0.01" min="0.01" value={form.manualSell} onChange={update('manualSell')}
            placeholder={form.priceMode === 'manual' ? 'Ex : 27.00' : 'Secours, optionnel'}
            required={form.priceMode === 'manual'} className={inputClass} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={form.enabled} onChange={update('enabled')} className="accent-yellow-500" />
        Serveur visible sur le site (pages, prix, formulaires de commande)
      </label>

      <div className="flex gap-3">
        <button type="submit" disabled={saving}
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold text-sm disabled:opacity-50">
          {saving ? 'Enregistrement…' : server ? 'Enregistrer' : 'Ajouter le serveur'}
        </button>
        <button type="button" onClick={() => onDone(false)} className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 text-sm">Annuler</button>
      </div>
    </form>
  );
}

export default function AdminServers({ token }) {
  const [servers, setServers] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | {} (new) | server
  const [needsLogin, setNeedsLogin] = useState(false);

  const load = useCallback(() => {
    adminListServers(token)
      .then(setServers)
      .catch(e => {
        if (e.status === 401 || e.status === 403) setNeedsLogin(true);
        else setError(e.message);
      });
  }, [token]);

  useEffect(load, [load]);

  const toggleEnabled = async (server) => {
    try {
      await adminSaveServer(token, server.id, { enabled: !server.enabled });
      load();
    } catch (e) { setError(e.message); }
  };

  const remove = async (server) => {
    if (!window.confirm(`Supprimer ${server.name} ? Sa page et ses prix disparaîtront du site.`)) return;
    try {
      await adminDeleteServer(token, server.id);
      load();
    } catch (e) { setError(e.message); }
  };

  if (needsLogin) return <SessionExpired />;
  if (!servers) return <CoinLoader label="Chargement des serveurs…" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-400">
          {servers.length} serveurs · {servers.filter(s => s.enabled).length} visibles ·{' '}
          {servers.filter(s => s.price_mode === 'manual').length} en prix manuels
        </p>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 text-sm flex items-center gap-1.5">
            <RefreshCw size={15} /> Actualiser
          </button>
          <button onClick={() => setEditing({})}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold text-sm flex items-center gap-1.5">
            <Plus size={16} /> Ajouter un serveur
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}

      {editing && <ServerEditor token={token} server={editing.id ? editing : null} onDone={(saved) => { setEditing(null); if (saved) load(); }} />}

      {GAMES.map(game => {
        const list = servers.filter(s => s.game === game);
        return (
          <div key={game} className="bg-gray-900/60 border border-gray-800 rounded-2xl overflow-hidden">
            <h3 className="px-4 py-3 text-sm sm:text-base font-bold text-white bg-gray-900 border-b border-gray-800">
              Kamas {GAME_LABELS[game]} <span className="text-gray-500 font-normal">· {list.length} serveurs</span>
            </h3>
            {list.length === 0 && <p className="px-4 py-3 text-sm text-gray-500">Aucun serveur.</p>}
            <ul className="divide-y divide-gray-800">
              {list.map(s => (
                <li key={s.id} className={`px-4 py-3 ${s.enabled ? '' : 'opacity-50'}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                        {s.name}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${s.price_mode === 'manual' ? 'bg-orange-500/20 text-orange-300' : 'bg-green-500/20 text-green-300'}`}>
                          {s.price_mode === 'manual' ? 'Manuel' : 'Auto'}
                        </span>
                        {!s.enabled && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">Masqué</span>}
                      </p>
                      <p className="text-xs text-gray-500">{s.slug} · {s.type}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Link to={serverPath(s)} className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white" title="Voir la page">
                        <ExternalLink size={15} />
                      </Link>
                      <button onClick={() => setEditing(s)} className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-yellow-400" title="Modifier">
                        <Edit2 size={15} />
                      </button>
                      <button onClick={() => remove(s)} className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-red-400" title="Supprimer">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs sm:text-sm">
                    <p className="text-gray-400">Achat client : <span className="text-green-400 font-semibold">{priceText(s.buy)}</span>
                      {s.competitor?.buy != null && <span className="text-gray-600"> (TJ {priceText(s.competitor.buy)})</span>}
                    </p>
                    <p className="text-gray-400">Vente client : <span className="text-red-400 font-semibold">{priceText(s.sell)}</span>
                      {s.competitor?.sell != null && <span className="text-gray-600"> (TJ {priceText(s.competitor.sell)})</span>}
                    </p>
                    <p className="text-gray-400">Manuel : <span className="text-gray-300">{priceText(s.manual_buy)} / {priceText(s.manual_sell)}</span></p>
                    <label className="flex items-center gap-2 text-gray-400 cursor-pointer">
                      <input type="checkbox" checked={!!s.enabled} onChange={() => toggleEnabled(s)} className="accent-yellow-500" />
                      Visible
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <p className="text-xs text-gray-600 flex items-center gap-1.5">
        <ArrowLeft size={12} className="rotate-45" />
        Mode auto : prix calculés depuis TryAndJudge (jusqu'à −1 DH achat / +1 DH vente, marge minimum garantie).
        Mode manuel : vos prix remplacent le calcul. Les changements sont visibles sur le site immédiatement.
      </p>
    </div>
  );
}
